import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, Label
} from 'recharts';

const RetentionLifecyclePage = () => {
  const [churnData, setChurnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChurnData();
  }, []);

  const fetchChurnData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/app-analytics/churn-indicators`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setChurnData(response.data);
    } catch (err) {
      console.error('Failed to fetch churn data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading retention analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Retention & Lifecycle Metrics</h1>
          <p className="text-purple-200 text-sm">User retention, churn prediction, and lifecycle analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchChurnData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retry</button>
        </div>
      </div>
    );
  }

  const getRiskColor = (level) => {
    switch(level) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      case 'critical': return '#991b1b';
      default: return '#6b7280';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <h1 className="text-3xl font-bold">Retention & Lifecycle Metrics</h1>
        <p className="text-purple-200 text-sm">User retention, churn prediction, and lifecycle analytics</p>
      </div>

      {/* Churn Prediction Indicators */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Churn Prediction Indicators</h3>
          <p className="text-sm text-gray-500">User activity status based on days since last active</p>
        </div>

        {churnData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-700 font-medium mb-1">Total Users</p>
                <p className="text-3xl font-bold text-gray-600">{churnData.total_users}</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-700 font-medium mb-1">Active Users</p>
                <p className="text-3xl font-bold text-green-600">{churnData.active_users}</p>
                <p className="text-xs text-green-600 mt-1">
                  {((churnData.active_users / churnData.total_users) * 100).toFixed(1)}%
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                <p className="text-sm text-yellow-700 font-medium mb-1">At Risk</p>
                <p className="text-3xl font-bold text-yellow-600">{churnData.at_risk_count}</p>
                <p className="text-xs text-yellow-600 mt-1">7-14 days inactive</p>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                <p className="text-sm text-orange-700 font-medium mb-1">Churning</p>
                <p className="text-3xl font-bold text-orange-600">{churnData.churning_count}</p>
                <p className="text-xs text-orange-600 mt-1">14-30 days inactive</p>
              </div>
              
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700 font-medium mb-1">Churned</p>
                <p className="text-3xl font-bold text-red-600">{churnData.churned_count}</p>
                <p className="text-xs text-red-600 mt-1">30+ days inactive</p>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={churnData.churn_indicators}
                  layout="horizontal"
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="category" 
                    stroke="#6b7280"
                    angle={-15}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis stroke="#6b7280">
                    <Label value="User Count" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                  </YAxis>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                    formatter={(value, name, props) => {
                      return [`${value} users (${props.payload.percentage}%)`, props.payload.category];
                    }}
                  />
                  <Bar dataKey="user_count" radius={[4, 4, 0, 0]}>
                    {churnData.churn_indicators.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getRiskColor(entry.risk_level)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Risk Level Legend */}
            <div className="flex flex-wrap gap-4 text-sm mb-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span>Low Risk - Active (0-7 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-500"></div>
                <span>Medium Risk - At Risk (7-14 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-500"></div>
                <span>High Risk - Churning (14-30 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{backgroundColor: '#991b1b'}}></div>
                <span>Critical - Churned (30+ days)</span>
              </div>
            </div>

            {/* Insights */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>📊</span> Churn Insights
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>
                  • <strong>{((churnData.active_users / churnData.total_users) * 100).toFixed(1)}%</strong> of users are active (logged in within 7 days)
                  {((churnData.active_users / churnData.total_users) * 100) >= 70 && <span className="text-green-600 ml-2">✓ Healthy retention</span>}
                  {((churnData.active_users / churnData.total_users) * 100) < 50 && <span className="text-red-600 ml-2">⚠ Low retention - investigate</span>}
                </li>
                <li>
                  • <strong>{churnData.at_risk_count + churnData.churning_count}</strong> users at risk of churning
                  {churnData.at_risk_count + churnData.churning_count > 0 && <span className="text-yellow-600 ml-2">Consider re-engagement campaign</span>}
                </li>
                <li>
                  • <strong>{churnData.churned_count}</strong> users haven't been active in 30+ days
                  {churnData.churned_count > (churnData.total_users * 0.2) && <span className="text-red-600 ml-2">⚠ High churn rate (>20%)</span>}
                </li>
                {churnData.at_risk_count > 0 && (
                  <li className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <strong className="text-yellow-900">💡 Action Items:</strong>
                    <ul className="mt-2 ml-4 text-yellow-800 space-y-1">
                      <li>• Send re-engagement notifications to {churnData.at_risk_count} at-risk users</li>
                      <li>• Survey inactive users to understand drop-off reasons</li>
                      <li>• Review feature usage patterns for churned users</li>
                    </ul>
                  </li>
                )}
              </ul>
            </div>

            {/* Info Section */}
            <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700"><strong>What this shows:</strong> Distribution of users by activity recency, helping identify churn risk.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Active users (green) are engaged. At Risk (yellow) need engagement. Churning (orange) are leaving. Churned (red) are gone - consider win-back campaigns.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Benchmarks:</strong> 70%+ active is healthy. 20%+ churned is critical. Focus re-engagement on "At Risk" segment for best ROI.</p>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p>No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetentionLifecyclePage;
